/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

/* eslint-disable react/no-unescaped-entities */

import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, Typography, Alert, Paper, Divider } from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

import {
  StudioEditFormProps,
  StudioFormData,
  StudioFormErrors,
} from '../../../types/studioForms';
import {
  createStudioFormDataFromStudio,
  validateStudioForm,
  isStudioFormValid,
  validateBusinessInfoField,
} from '../../../utils/studioValidation';
import { StudioFormFields } from './StudioFormFields';

export const StudioEditForm: React.FC<StudioEditFormProps> = ({
  studio,
  onSubmit,
  onCancel,
  loading = false,
  error = null,
}) => {
  // Form state
  const [formData, setFormData] = useState<StudioFormData>(
    createStudioFormDataFromStudio(studio)
  );
  const [errors, setErrors] = useState<StudioFormErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  // Initialize form data when studio changes
  useEffect(() => {
    const initialData = createStudioFormDataFromStudio(studio);
    setFormData(initialData);
    setIsDirty(false);
    setErrors({});
  }, [studio]);

  // Handle field changes
  const handleFieldChange = useCallback(
    (field: keyof StudioFormData, value: any) => {
      setFormData(prev => {
        const newData = {
          ...prev,
          [field]: value,
        };

        // Check if form is dirty
        const initialData = createStudioFormDataFromStudio(studio);
        const isFormDirty =
          JSON.stringify(newData) !== JSON.stringify(initialData);
        setIsDirty(isFormDirty);

        return newData;
      });

      // Clear error when user starts typing
      if (field !== 'businessInfo' && errors[field as keyof StudioFormErrors]) {
        setErrors(prev => ({
          ...prev,
          [field]: undefined,
        }));
      }

      // Real-time validation for business info fields
      if (field === 'businessInfo' && typeof value === 'object') {
        const newErrors = { ...errors };
        Object.entries(value).forEach(([subField, subValue]) => {
          if (typeof subValue === 'string') {
            const fieldError = validateBusinessInfoField(
              subField as 'address' | 'phone' | 'website',
              subValue
            );
            if (fieldError) {
              newErrors[subField as keyof StudioFormErrors] = fieldError;
            } else {
              delete newErrors[subField as keyof StudioFormErrors];
            }
          }
        });
        setErrors(newErrors);
      }
    },
    [errors, studio]
  );

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validate entire form
    const validationErrors = validateStudioForm(formData);
    setErrors(validationErrors);

    // Check if form is valid
    if (!isStudioFormValid(validationErrors)) {
      return;
    }

    try {
      // Only send changed fields
      const initialData = createStudioFormDataFromStudio(studio);
      const changedData: Partial<StudioFormData> = {};

      if (formData.name !== initialData.name) {
        changedData.name = formData.name;
      }
      if (formData.description !== initialData.description) {
        changedData.description = formData.description;
      }
      if (formData.logo !== initialData.logo) {
        changedData.logo = formData.logo;
      }

      // Check business info changes
      const businessInfoChanged = Object.keys(formData.businessInfo).some(
        key =>
          formData.businessInfo[key as keyof typeof formData.businessInfo] !==
          initialData.businessInfo[key as keyof typeof initialData.businessInfo]
      );

      if (businessInfoChanged) {
        changedData.businessInfo = formData.businessInfo;
      }

      // Check allowJoinRequests changes
      if (formData.allowJoinRequests !== initialData.allowJoinRequests) {
        changedData.allowJoinRequests = formData.allowJoinRequests;
      }

      // Check joinRequestMessage changes
      if (formData.joinRequestMessage !== initialData.joinRequestMessage) {
        changedData.joinRequestMessage = formData.joinRequestMessage;
      }

      // Check timezone changes
      if (formData.timezone !== initialData.timezone) {
        changedData.timezone = formData.timezone;
      }

      // Check studioType changes
      if (formData.studioType !== initialData.studioType) {
        changedData.studioType = formData.studioType;
      }

      await onSubmit(changedData);
      setIsDirty(false);
    } catch (submitError) {
      // Error handling is managed by parent component
      console.error('Form submission error:', submitError);
    }
  };

  // Handle cancel with unsaved changes warning
  const handleCancel = () => {
    if (isDirty) {
      setShowUnsavedWarning(true);
    } else if (onCancel) {
      onCancel();
    }
  };

  // Confirm cancel with unsaved changes
  const handleConfirmCancel = () => {
    setShowUnsavedWarning(false);
    setIsDirty(false);
    if (onCancel) {
      onCancel();
    }
  };

  // Handle beforeunload event for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue =
          'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Edit Studio
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Update your studio information and settings.
      </Typography>

      {/* Unsaved Changes Warning */}
      {showUnsavedWarning && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                color="inherit"
                size="small"
                onClick={() => setShowUnsavedWarning(false)}
              >
                Continue Editing
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={handleConfirmCancel}
              >
                Discard Changes
              </Button>
            </Box>
          }
        >
          You have unsaved changes. Are you sure you want to cancel?
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Dirty State Indicator */}
      {isDirty && !showUnsavedWarning && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have unsaved changes. Don't forget to save your updates.
        </Alert>
      )}

      {/* Form */}
      <Paper elevation={1} sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <StudioFormFields
            formData={formData}
            onChange={handleFieldChange}
            errors={errors}
            disabled={loading}
            studio={studio}
          />

          <Divider sx={{ my: 4 }} />

          {/* Form Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 4,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={loading || !isDirty}
              startIcon={<SaveIcon />}
              sx={{
                minWidth: 120,
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Help Text */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>About editing your studio:</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          • Changes are saved immediately when you click &quot;Save
          Changes&quot;
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Your studio URL and member access won't be affected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Logo changes may take a few minutes to appear everywhere
        </Typography>
      </Box>
    </Box>
  );
};

export default StudioEditForm;
